import registry from './councils.registry.json';
import londonAuthorities from './londonAuthorities.json';

export type ScottishCouncilSlug = keyof typeof registry;
export type LondonCouncilSlug = keyof typeof londonAuthorities;
export type CouncilSlug = ScottishCouncilSlug | LondonCouncilSlug;
export type CouncilShortCode = string;

export type CouncilConfig = {
  slug: CouncilSlug;
  shortCode: CouncilShortCode;
  displayName: string;
  officialName: string;
  authorityCode: string;
  sourceSystem: 'idox' | 'legacy-asp-civica' | 'official-records';
  portalBaseUrl: string;
  portalPathPrefix: string;
  publicSearchUrl: string;
  defaultCenter: { lng: number; lat: number };
  bbox: { west: number; south: number; east: number; north: number };
  geocoderBias: string;
  supportsBulkDownload: boolean;
  legacyDefault: boolean;
  publicEnabled: boolean;
  releaseStage?: 'enriched' | 'official_records';
  planningDelivery?: 'shared' | 'retained-london';
  sourceCoverage?: {
    dateBasis: 'received' | 'validated';
    windowStart: string;
    windowEndExclusive: string;
    verifiedAt: string;
    sourceCount: number | null;
    capturedCount: number;
    missingCount: number | null;
    status: 'complete' | 'partial' | 'unverified';
    notice?: string;
  };
  enrichment?: {
    status: 'in_progress' | 'complete';
    verifiedAt: string;
    qualifiedCount: number;
    totalCount: number;
  };
  sourcePortalUrl?: string;
  region?: 'london';
};

// Keep the Scottish registry separate: its consumers use this collection to
// build Scotland-wide queries, boundaries and release counts.
export const COUNCILS = registry as Record<ScottishCouncilSlug, CouncilConfig>;

export const LONDON_COUNCILS = Object.fromEntries(
  Object.entries(londonAuthorities).map(([slug, authority]) => [slug, {
    slug,
    shortCode: slug,
    displayName: authority.displayName,
    officialName: authority.displayName,
    authorityCode: authority.authorityCode,
    sourceSystem: 'official-records',
    // Each application retains its own verified source URL. An unknown portal
    // URL must not be inferred from a council name or another council's portal.
    portalBaseUrl: '', portalPathPrefix: '', publicSearchUrl: '',
    // These are regional camera defaults, never council boundaries or case locations.
    defaultCenter: { lng: -.1, lat: 51.5 },
    bbox: { west: -.7, south: 51.1, east: .5, north: 51.9 },
    geocoderBias: '', supportsBulkDownload: false, legacyDefault: false,
    publicEnabled: true, releaseStage: 'official_records', region: 'london', planningDelivery: 'retained-london',
  }])
) as Record<LondonCouncilSlug, CouncilConfig>;

const COUNCIL_LOOKUP = { ...COUNCILS, ...LONDON_COUNCILS };
const COUNCIL_ALIASES = Object.values(COUNCIL_LOOKUP).reduce<Record<string, CouncilSlug>>((acc, council) => {
  acc[council.shortCode] = council.slug;
  acc[council.slug] = council.slug;
  acc[council.slug.replace(/-/g, '_')] = council.slug;
  return acc;
}, {});

export const DEFAULT_COUNCIL_SLUG: CouncilSlug = 'edinburgh';
export const AGGREGATE_COUNCIL_SLUG = 'scotland' as const;
export type AggregateCouncilSlug = typeof AGGREGATE_COUNCIL_SLUG;

export const isAggregateCouncilScope = (value?: string | null) => {
  const raw = String(value || '').trim().toLowerCase();
  return raw === AGGREGATE_COUNCIL_SLUG || raw === 'all';
};

export const normalizeCouncilSlug = (value?: string | null): CouncilSlug | null => {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return DEFAULT_COUNCIL_SLUG;
  const slug = Object.hasOwn(COUNCIL_ALIASES, raw) ? COUNCIL_ALIASES[raw] : raw;
  return Object.hasOwn(COUNCIL_LOOKUP, slug) ? (slug as CouncilSlug) : null;
};

export const getCouncilConfig = (value?: string | null): CouncilConfig | null => {
  const slug = normalizeCouncilSlug(value);
  return slug ? COUNCIL_LOOKUP[slug] : null;
};

export const LONDON_REGION_CONFIG = {
  slug: 'london', shortCode: 'london', displayName: 'London',
  officialName: 'London planning authorities', region: 'london',
  defaultCenter: { lng: -.1, lat: 51.5 },
  bbox: { west: -.7, south: 51.1, east: .5, north: 51.9 },
  publicEnabled: true, legacyDefault: false, releaseStage: 'official_records',
} as const;

export const isLondonCouncil = (value?: string | null) => getCouncilConfig(value)?.region === 'london';

export const councilSlugToShortCode = (value?: string | null): CouncilShortCode => {
  const council = getCouncilConfig(value) || COUNCILS.edinburgh;
  return council.shortCode;
};

export const isLegacyCouncil = (value?: string | null) =>
  (getCouncilConfig(value) || COUNCILS.edinburgh).legacyDefault;

const normalizeApplicationReference = (value: unknown) => String(value || '')
  .replace(/\s+/g, '')
  .trim()
  .toUpperCase();

export const getApplicationReference = (record: Record<string, unknown>) =>
  String(
    record.reference_no ||
    record.reference ||
    record.ref ||
    record.application_ref ||
    record.id ||
    ''
  ).trim();

export const formatCouncilQualifiedReference = (
  reference: unknown,
  council?: string | null
) => {
  const rawReference = String(reference || '').trim();
  if (!rawReference) return '';

  const segments = rawReference.split('/').map((segment) => segment.trim()).filter(Boolean);
  const prefixedCouncil = segments.length > 1 ? getCouncilConfig(segments[0]) : null;
  const resolvedCouncil = prefixedCouncil || getCouncilConfig(council);
  const officialReference = prefixedCouncil ? segments.slice(1).join('/') : rawReference;

  if (!resolvedCouncil) return officialReference;
  return `${resolvedCouncil.shortCode.toUpperCase()}/${officialReference}`;
};

export const getCouncilQualifiedApplicationReference = (
  record: Record<string, unknown>,
  fallbackCouncil?: string | null
) => {
  const reference = getApplicationReference(record);
  const sourceCouncil = String(
    record.source_council ||
    record.council_slug ||
    record.council ||
    record.sourceCouncil ||
    fallbackCouncil ||
    DEFAULT_COUNCIL_SLUG
  ).trim();

  return formatCouncilQualifiedReference(reference, sourceCouncil);
};

export const applicationIdentityKey = (record: Record<string, unknown>) => {
  const explicit = String(record.application_uid || '').trim();
  if (explicit) return explicit.toLowerCase();
  const rawSourceCouncil = String(
    record.source_council ||
    record.council_slug ||
    record.council ||
    record.sourceCouncil ||
    DEFAULT_COUNCIL_SLUG
  ).trim().toLowerCase();
  const sourceCouncil = getCouncilConfig(rawSourceCouncil)?.slug || rawSourceCouncil;
  const reference = normalizeApplicationReference(getApplicationReference(record));
  const recordKey = String(record.record_key || '').trim().toLowerCase();
  if (!reference && recordKey) return recordKey;
  return reference ? `${sourceCouncil}:${reference}`.toLowerCase() : '';
};
